import LocationHelpLabel from './LocationHelpLabel';
import LocationToggle from './LocationToggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import { useRadioStationsForDropdown } from '@/hooks/useRadioStation';
import { useTranslation } from '@/hooks/useTranslation';
import { AdCampaignType, RadioAdDisplayMode } from '@/types/ads';

interface Props {
  type?: AdCampaignType | null;
  enabled: boolean;
  mode: RadioAdDisplayMode;
  included: string[];
  excluded: string[];
  readOnly?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  hideTargeting?: boolean;
  targetingOnly?: boolean;
  collapsibleHelp?: boolean;
  onEnabledChange?: (value: boolean) => void;
  onModeChange?: (value: RadioAdDisplayMode) => void;
  onIncludedChange?: (value: string[]) => void;
  onExcludedChange?: (value: string[]) => void;
}

export default function RadioCampaignFields({
  type, enabled, included, excluded, readOnly = false, disabled = false, disabledReason, hideTargeting = false, targetingOnly = false, collapsibleHelp = false,
  onEnabledChange, onIncludedChange, onExcludedChange,
}: Props) {
  const { t } = useTranslation();
  const labels = t.radioCampaign;
  const stations = useRadioStationsForDropdown(enabled && type !== 'VIDEO');
  const options = (stations.data ?? []).map((station) => ({ value: station.id, label: station.stationName }));
  // Keep saved IDs visible even when a station was deleted or the lookup fails.
  const knownIds = new Set(options.map((option) => option.value));
  for (const id of new Set([...included, ...excluded])) {
    if (!knownIds.has(id)) options.push({ value: id, label: id });
  }
  const names = (ids: string[], fallback: string) => ids.length
    ? ids.map((id) => options.find((option) => option.value === id)?.label ?? id).join(', ')
    : fallback;
  const overlap = included.some((id) => excluded.includes(id));

  return (
    <div className="space-y-4 border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          {collapsibleHelp ? <LocationHelpLabel label={labels.title} description={type === 'VIDEO' ? labels.video : labels.placement} /> : <>
            <h3 className="text-sm font-medium text-gray-900">{labels.title}</h3>
            {!targetingOnly && <p className="text-xs text-gray-500">{type === 'VIDEO' ? labels.video : labels.placement}</p>}
          </>}
        </div>
        {!targetingOnly && (readOnly ? <span className="text-sm">{enabled ? labels.enabled : labels.disabled}</span> : (
          <LocationToggle aria-label={labels.enabled} className="shrink-0" checked={type === 'VIDEO' ? false : enabled}
            disabled={disabled || type === 'VIDEO'} disabledReason={type === 'VIDEO' ? labels.video : disabledReason}
            onChange={(value) => onEnabledChange?.(value)} size="sm" />
        ))}
      </div>
      {enabled && type !== 'VIDEO' && (
        <div className="space-y-3">
          {!hideTargeting && <>
          <p className="text-xs text-gray-500">{labels.targeting}</p>
          {stations.isLoading && <p className="text-sm" role="status">{labels.loading}</p>}
          {stations.isError && (
            <div className="space-y-2" role="alert">
              <p className="text-sm text-red-600">{labels.loadError}</p>
              <Button type="button" size="sm" variant="outline" onClick={() => void stations.refetch()}>{labels.retry}</Button>
            </div>
          )}
          {readOnly ? (
            <div className="space-y-2 text-sm">
              <p>{labels.include}: {names(included, labels.all)}</p>
              <p>{labels.exclude}: {names(excluded, labels.none)}</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <MultiSelect label={labels.include} placeholder={labels.select} options={options} value={included}
                disabled={stations.isLoading || stations.isError} onChange={(value) => onIncludedChange?.(value)} />
              <MultiSelect label={labels.exclude} placeholder={labels.select} options={options} value={excluded}
                disabled={stations.isLoading || stations.isError} onChange={(value) => onExcludedChange?.(value)}
                error={overlap ? labels.overlap : undefined} />
            </div>
          )}
          <p className="text-xs text-gray-500">{labels.location}</p>
          </>}
        </div>
      )}
    </div>
  );
}
